import Axios, { type AxiosError } from "axios";
import { useAtom } from "jotai";
import { type UseQueryOptions, useQuery, useQueryClient } from "react-query";
import { runYearAtom } from "src/atoms/overlayers";
import {
	fetchRunYears,
	type RunYearInfo,
	runYearsQueryKey,
} from "src/hooks/runYearHooks";
import { caseKeys } from "src/query-keys";
import type { APIError, Case, CaseBase } from "src/types";

/**
 * Deserialize the json format into a Javascript object
 */
function deserialize(data: CaseBase | Case): CaseBase | Case {
	return {
		...data,
		dateCreated: new Date(data.dateCreated),
	};
}

export function useCase(
	caseId: string | number,
	options?: UseQueryOptions<Case, AxiosError<APIError[]>, Case>,
) {
	const [, setRunYear] = useAtom(runYearAtom);
	const queryClient = useQueryClient();

	async function fetch(id: string | number): Promise<Case> {
		const [caseResponse, runYears] = await Promise.all([
			Axios.get<Case>(`/planning-api/cases/${id}`),
			queryClient.fetchQuery<RunYearInfo[]>(runYearsQueryKey, fetchRunYears),
		]);
		const selectedRunYear = runYears.find(
			(runYear) => runYear.year === caseResponse.data.runYear,
		);
		setRunYear(
			selectedRunYear
				? { ...selectedRunYear, cutoff: caseResponse.data.cutoff }
				: undefined,
		);
		return deserialize(caseResponse.data) as Case;
	}

	return useQuery<Case, AxiosError<APIError[]>>(
		caseKeys.detail(caseId),
		() => fetch(caseId),
		{
			...options,
			// staleTime: 60 * 60 * 1000,
		},
	);
}
